import React, { useCallback } from 'react';
import { Col, Form, Input, Row, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { TranslateFormLabel } from 'components/translateInputAddon';

const areaStyles = {
  height: 150,
};

const MAX_DESCRIPTION_LENGTH = 350;

const DescriptionTextArea = React.forwardRef(
  (
    {
      fieldName,
      form,
      getDescriptionErrors,
      onBlur,
      onFocus,
      ...rest
    },
    ref,
  ) => {
    const handleBlur = (event) => {
      onBlur?.(event);
      const errors = getDescriptionErrors(fieldName, event.target.value);
      form.setFields([{ name: fieldName, errors }]);
    };

    const handleFocus = (event) => {
      onFocus?.(event);
      form.setFields([{ name: fieldName, errors: [] }]);
    };

    return (
      <Input.TextArea
        ref={ref}
        style={areaStyles}
        onBlur={handleBlur}
        onFocus={handleFocus}
        {...rest}
      />
    );
  },
);

DescriptionTextArea.displayName = 'DescriptionTextArea';

const DescriptionSection = ({ isView, form }) => {
  const { t } = useTranslation();

  const maxLengthMessage = t(
    'La descrizione non può superare i 350 caratteri',
  );
  const requiredMessage = t('Descrizione non può essere lasciato in bianco');

  const maxLengthRule = {
    validator: (_, value) => {
      if (value && value.length > MAX_DESCRIPTION_LENGTH) {
        return Promise.reject(new Error(maxLengthMessage));
      }
      return Promise.resolve();
    },
  };

  const getDescriptionErrors = useCallback(
    (fieldName, value) => {
      const text = value ?? '';
      const errors = [];

      if (fieldName === 'description_it' && !text.trim()) {
        errors.push(requiredMessage);
      }
      if (text.length > MAX_DESCRIPTION_LENGTH) {
        errors.push(maxLengthMessage);
      }

      return errors;
    },
    [maxLengthMessage, requiredMessage],
  );

  const textAreaProps = useCallback(
    (fieldName) => ({
      fieldName,
      form,
      getDescriptionErrors,
      disabled: isView,
      placeholder: t('Inserisci testo'),
    }),
    [form, getDescriptionErrors, isView, t],
  );

  return (
    <Row gutter={24}>
      <Col span={6}>
        <Typography.Title level={3}>{t('Descrizione')}</Typography.Title>
      </Col>
      <Col span={6}>
        <Form.Item
          label={
            <TranslateFormLabel
              label={t('Descrizione IT (max 350 caratteri spazi inclusi)')}
              form={form}
              sourceField='description_it'
              textKey='description'
              targetFields={{
                en: 'description_en',
                fr: 'description_fr',
              }}
              disabled={isView}
            />
          }
          name='description_it'
          rules={[
            {
              required: true,
              message: requiredMessage,
            },
            maxLengthRule,
          ]}
        >
          <DescriptionTextArea {...textAreaProps('description_it')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          label={t('Descrizione EN max 350 caratteri spazi inclusi')}
          name='description_en'
          rules={[maxLengthRule]}
        >
          <DescriptionTextArea {...textAreaProps('description_en')} />
        </Form.Item>
      </Col>
      <Col span={6}>
        <Form.Item
          label={t('Descrizione FR max 350 caratteri spazi inclusi')}
          name='description_fr'
          rules={[maxLengthRule]}
        >
          <DescriptionTextArea {...textAreaProps('description_fr')} />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default React.memo(DescriptionSection);
